import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, App, Button, Checkbox, Drawer, Input, Space, Typography } from 'antd';
import {
  createSubscription,
  updateSubscription,
  type SecretGrant,
  type SubscriptionView,
} from '@/api/subscriptions';
import { normalizeApiError } from '@/api/errors';
import { InfoTip } from '@/help/InfoTip';
import { describeEventType } from './eventCatalogue';
import { explainWriteRefusal } from './webhookErrors';

const { Text, Paragraph } = Typography;

/** Drawer target: create a new subscription, or edit an existing one. */
export type SubscriptionDrawerTarget = 'create' | SubscriptionView | null;

/**
 * Create/edit form for an outbound subscription. Offers exactly what the API validates: a name,
 * an HTTPS endpoint (the server rejects http/loopback/private ranges — WEBHOOK_URL_INVALID), and
 * event types from the CLOSED server catalogue (nothing free-text: WEBHOOK_TYPE_UNKNOWN cannot be
 * typed into existence here). Writes are a step-up lane (subscriptions.write, hub_admin) — a
 * refusal renders as an explanation inside the drawer, verbatim server message included.
 * On create, the caller receives the ONE-TIME SecretGrant to show.
 */
export function SubscriptionDrawer({
  target,
  subscribableEventTypes,
  onClose,
  onSaved,
  onSecret,
}: {
  target: SubscriptionDrawerTarget;
  subscribableEventTypes: string[];
  onClose: () => void;
  /** Called after any successful write so the list reloads. */
  onSaved: () => void;
  /** Called with the one-time secret after a successful CREATE. */
  onSecret: (grant: SecretGrant) => void;
}) {
  const { message } = App.useApp();
  const editing = target !== null && target !== 'create' ? target : null;

  const [name, setName] = useState('');
  const [targetUrl, setTargetUrl] = useState('');
  const [eventTypes, setEventTypes] = useState<string[]>([]);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // A fresh target is a fresh act — stale form state never leaks between opens.
  useEffect(() => {
    setName(editing?.name ?? '');
    setTargetUrl(editing?.targetUrl ?? '');
    setEventTypes(editing?.eventTypes ?? []);
    setRefusal(null);
  }, [target, editing]);

  const complete = name.trim().length > 0 && targetUrl.trim().length > 0 && eventTypes.length > 0;

  const save = useCallback(async () => {
    setSaving(true);
    setRefusal(null);
    try {
      if (editing) {
        const view = await updateSubscription(editing.subscriptionId, {
          name: name.trim(),
          targetUrl: targetUrl.trim(),
          eventTypes,
        });
        message.success(`'${view.name}' updated — ${view.eventTypes.length} event type(s).`);
        onSaved();
        onClose();
      } else {
        const grant = await createSubscription({
          name: name.trim(),
          targetUrl: targetUrl.trim(),
          eventTypes,
        });
        message.success(`'${grant.subscription.name}' created — store the secret now.`);
        onSaved();
        onClose();
        onSecret(grant); // after close: the one-time secret gets the full stage
      }
    } catch (e) {
      setRefusal(explainWriteRefusal(normalizeApiError(e)));
    } finally {
      setSaving(false);
    }
  }, [editing, name, targetUrl, eventTypes, message, onSaved, onClose, onSecret]);

  const options = useMemo(
    () =>
      subscribableEventTypes.map((t) => {
        const desc = describeEventType(t);
        return { type: t, meaning: desc?.meaning ?? null };
      }),
    [subscribableEventTypes],
  );

  return (
    <Drawer
      open={target !== null}
      onClose={onClose}
      width={560}
      title={editing ? `Edit ${editing.name}` : 'New event subscription'}
      extra={
        <Button type="primary" onClick={() => void save()} loading={saving} disabled={!complete}>
          {editing ? 'Save changes' : 'Create subscription'}
        </Button>
      }
    >
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        {!editing && (
          <Paragraph type="secondary" style={{ margin: 0, fontSize: 12.5 }}>
            Creating a subscription mints a signing secret, shown ONCE on success. Silence is the
            default: this endpoint will receive only the event types ticked below, signed, with
            at-least-once delivery (dedup on the event id at your end).
          </Paragraph>
        )}

        <label style={{ display: 'block' }}>
          <div style={{ fontSize: 12, marginBottom: 4 }}>
            Name{' '}
            <Text type="secondary" style={{ fontSize: 11 }}>
              (unique per hub)
            </Text>
          </div>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. core-banking-bridge"
            maxLength={120}
          />
        </label>

        <label style={{ display: 'block' }}>
          <div style={{ fontSize: 12, marginBottom: 4, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            Endpoint URL <InfoTip surface="integrations" field="Endpoint URL" />
          </div>
          <Input
            value={targetUrl}
            onChange={(e) => setTargetUrl(e.target.value)}
            placeholder="https://receiver.example.com/opsflow/events"
            maxLength={600}
          />
          <Text type="secondary" style={{ fontSize: 11 }}>
            HTTPS only; the server rejects loopback, private ranges and redirects.
          </Text>
        </label>

        <div>
          <div style={{ fontSize: 12, marginBottom: 6, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            Event types <InfoTip surface="integrations" field="Event types" />
            <Text type="secondary" style={{ fontSize: 11 }}>
              — {eventTypes.length} of {options.length} selected (at least one required)
            </Text>
          </div>
          {options.length === 0 ? (
            <Alert
              type="info"
              showIcon
              message="The server returned no subscribable event types, so nothing can be selected here."
            />
          ) : (
            <div
              role="group"
              aria-label="Subscribable event types"
              style={{
                maxHeight: 320,
                overflow: 'auto',
                border: '1px solid var(--ol-border)',
                borderRadius: 6,
                padding: '6px 10px',
              }}
            >
              {options.map(({ type, meaning }) => (
                <div key={type} style={{ padding: '5px 0' }}>
                  <Checkbox
                    checked={eventTypes.includes(type)}
                    onChange={(e) =>
                      setEventTypes((cur) =>
                        e.target.checked ? [...cur, type] : cur.filter((t) => t !== type),
                      )
                    }
                  >
                    <span className="ol-mono" style={{ fontSize: 12 }}>
                      {type}
                    </span>
                  </Checkbox>
                  <div style={{ marginLeft: 24 }}>
                    {meaning ? (
                      <Text type="secondary" style={{ fontSize: 11.5 }}>
                        {meaning}
                      </Text>
                    ) : (
                      <Text type="secondary" italic style={{ fontSize: 11.5 }}>
                        No description in this console yet — the id is authoritative.
                      </Text>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {refusal && <Alert type="error" showIcon message="Not saved" description={refusal} />}
      </Space>
    </Drawer>
  );
}
